import { isError, last } from "lodash";
import { isBrowserExtensionError } from "@/utils/browserExtensions";
import { filterText } from "./filterDomain";
import { SerializedError, serializeError, serializeValue } from "./serializeDomain";

export type LogEventLevel = "warn" | "error";

export type ParsedLogArgs = { cause?: unknown; message: string; data: unknown[] };

export type LogData = {
  level: LogEventLevel;
  message: string;
  error?: SerializedError;
  cause?: unknown;
  args: unknown[];
};

const UNKNOWN_ERROR = "Unknown error";

export function parseLogArgs(args: unknown[]): ParsedLogArgs {
  const [first, ...rest] = args;
  switch (true) {
    case args.length === 0:
      return { message: UNKNOWN_ERROR, data: [] };
    case typeof first !== "string":
      return { cause: first, message: String(rest[0] ?? UNKNOWN_ERROR), data: rest.slice(1) };
    case isError(last(rest)):
      return { cause: last(rest), message: first, data: rest.slice(0, -1) };
    default:
      return { message: first, data: rest };
  }
}

export function serializeCause(cause: unknown): Pick<LogData, "error" | "cause"> {
  switch (true) {
    case cause === undefined:
      return {};
    case isError(cause):
      return { error: serializeError(cause) };
    default:
      return { cause: serializeValue(cause) };
  }
}

export function isReportableLog(args: unknown[]): boolean {
  return !isBrowserExtensionError(parseLogArgs(args).cause);
}

export function toLogData(level: LogEventLevel, args: unknown[]): LogData {
  const { cause, message, data } = parseLogArgs(args);
  return {
    level,
    message: filterText(message),
    ...serializeCause(cause),
    args: data.map((arg) => serializeValue(arg)),
  };
}
